import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LineupImportsBatchStatus } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheckCircle } from '@sorare/core/src/atoms/icons/system/iconCheckCircle.ts';
import { iconChevronUp } from '@sorare/core/src/atoms/icons/system/iconChevronUp.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const TIME_BEFORE_DISAPPEAR = 10000;

const Root = styled.div`
  @media ${tabletAndAbove} {
    position: fixed;
    bottom: 0;
    right: var(--double-unit);
  }
`;

const StyledButtonBase = styled(ButtonBase)`
  width: 100%;

  .disappearing {
    transition: opacity ${TIME_BEFORE_DISAPPEAR}ms;
    opacity: 0;
  }

  @media ${tabletAndAbove} {
    margin-bottom: var(--double-unit);
  }
`;

const Wrapper = styled(Horizontal)`
  height: 32px;
  width: 100%;
  box-shadow: var(--shadow-100);
  background-color: var(--c-nd-100);
  border-radius: var(--double-unit);
  padding: var(--half-unit) var(--double-unit);
  justify-content: space-between;
  @media ${tabletAndAbove} {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const LoadingIcon = styled.div`
  width: var(--double-and-a-half-unit);
  height: var(--double-and-a-half-unit);
`;
type Props = {
  onClick: () => void;
  lineupCount: number;
  lineupsImportState: So5LineupImportsBatchStatus;
  onComplete: () => void;
};

const importReadyMessage = defineMessage({
  defaultMessage:
    'Start import of {lineupCount} {lineupCount, plural, one {lineup} other {lineups}}',
});

const importLineupShortMessage: Record<
  So5LineupImportsBatchStatus,
  {
    defaultMessage: string;
  } | null
> = {
  [So5LineupImportsBatchStatus.DECLINED]: null,
  [So5LineupImportsBatchStatus.EXPIRED]: null,
  [So5LineupImportsBatchStatus.PREPARING]: importReadyMessage,
  [So5LineupImportsBatchStatus.PREPARED]: importReadyMessage,
  [So5LineupImportsBatchStatus.PROCESSING]: defineMessage({
    defaultMessage:
      'Importing {lineupCount} {lineupCount, plural, one {lineup} other {lineups}}',
  }),
  [So5LineupImportsBatchStatus.PROCESSED]: defineMessage({
    defaultMessage:
      'Importing {lineupCount} {lineupCount, plural, one {lineup} other {lineups}} complete',
  }),
};

export const MinifiedImportToast = ({
  onClick,
  lineupCount,
  lineupsImportState,
  onComplete,
}: Props) => {
  const [disappearing, setDisappearing] = useState(false);
  const isTabletAndAbove = useIsTabletAndAbove();

  useEffect(() => {
    if (lineupsImportState === So5LineupImportsBatchStatus.PROCESSED) {
      setDisappearing(true);
      setTimeout(() => {
        onComplete();
      }, TIME_BEFORE_DISAPPEAR);
    }
  }, [lineupsImportState, onComplete]);

  if (
    [
      So5LineupImportsBatchStatus.PROCESSING,
      So5LineupImportsBatchStatus.PROCESSED,
      So5LineupImportsBatchStatus.PREPARING,
      So5LineupImportsBatchStatus.PREPARED,
    ].includesValue(lineupsImportState)
  )
    return (
      <Root>
        <ConditionalWrapper
          wrap={!isTabletAndAbove}
          Wrapper={Portal}
          props={{ id: 'above-bottom-bar-portal' }}
        >
          <StyledButtonBase
            onClick={onClick}
            className={classNames({ disappearing })}
          >
            <Wrapper center>
              <BodyS>
                <FormattedMessage
                  {...importLineupShortMessage[lineupsImportState]}
                  values={{ lineupCount }}
                />
              </BodyS>
              {lineupsImportState ===
                So5LineupImportsBatchStatus.PROCESSING && (
                <LoadingIcon>
                  <LoadingIndicator smaller />
                </LoadingIcon>
              )}
              {lineupsImportState === So5LineupImportsBatchStatus.PROCESSED && (
                <FontAwesomeIcon icon={iconCheckCircle} />
              )}
              {[
                So5LineupImportsBatchStatus.PREPARED,
                So5LineupImportsBatchStatus.PREPARING,
              ].includesValue(lineupsImportState) && (
                <FontAwesomeIcon icon={iconChevronUp} />
              )}
            </Wrapper>
          </StyledButtonBase>
        </ConditionalWrapper>
      </Root>
    );
  return null;
};
